import React, { useState } from "react";
import classNames from "classnames";
import axios from "axios";
import VehicleEdit from "./VehicleEdit";
import DeleteModal from "./DeleteModal";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from "react-toastify";

interface IPROPS {
  vehicleData: any[];
  setOpenVehicleModal: (open: boolean) => void;
  fetchVehicle: () => Promise<void>;
}

const VehicleTable: React.FC<IPROPS> = ({
  vehicleData,
  setOpenVehicleModal,
  fetchVehicle,
}) => {
  const token = localStorage.getItem("customer-token");
  const fetchingTenant = window.location.hostname.split(".")[0];
  const apiPort = process.env.REACT_APP_API_PROTOCOL;

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<any | null>(null);
  const [deletingVehicleData, setDeletingVehicleData] = useState<string | null>(
    null
  );

  const handleEdit = (vehicle: any) => {
    setEditingVehicle(vehicle);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingVehicle(null);
  };

  const handleDelete = (vehicle: any) => {
    setDeletingVehicleData(vehicle?.id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingVehicleData) return;
    setLoading(true);
    try {
      const response = await axios.delete(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/gc/vehicles/${deletingVehicleData}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data) {
        toast.success("Deleted successfully")
        await fetchVehicle(); 
      }
    } catch (error) {
      console.error("Error deleting vehicle:", error);
    } finally {
      setShowDeleteModal(false);
      setDeletingVehicleData(null);
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingVehicleData(null);
  };

  const vehicleModalHandler = () => {
    setOpenVehicleModal(true);
  };

  // 

  return (
    <div className="customer-vehicle-info__table-container">
      <table
        className="customer-vehicle-info__table"
        aria-label="Vehicles table"
      >
        <thead>
          <tr>
            <th>Brand</th>
            <th>Model</th>
            <th>Year</th>
            <th>Fuel</th>
            <th>Vehicle Registration Number</th>
            <th>Actions</th>
          </tr>
        </thead>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <tbody>
            {vehicleData.map((vehicle) => (
              <tr key={vehicle.id} className="vehicle-info__row">
                <td className="customer-vehicle-info__cell">
                  {vehicle.brand_name}
                </td>
                <td className="customer-vehicle-info__cell">
                  {vehicle.model_name}
                </td>
                <td className="customer-vehicle-info__cell">
                  {vehicle.make_year}
                </td>
                <td className="customer-vehicle-info__cell">
                  {vehicle.fuel_type}
                </td>
                <td className="customer-vehicle-info__cell">
                  {vehicle.vehicle_number}
                </td>
                <td className="customer-vehicle-info__cell vehicle-info__actions">
                  <button
                    style={{ borderRadius: "20px" }}
                    className={classNames(
                      "customer-vehicle-info__btn",
                      "customer-vehicle-info__btn--edit"
                    )}
                    onClick={() => handleEdit(vehicle)}
                  >
                    Edit
                  </button>
                  <button
                    style={{ borderRadius: "20px" }}
                    className={classNames(
                      "customer-vehicle-info__btn",
                      "customer-vehicle-info__btn--delete"
                    )}
                    onClick={() => handleDelete(vehicle)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>

      {/* ---------- Mobile Card View ---------- */}

      <div className="vehicle-cards" role="list">
        <span>
          <div style={{ marginTop: "5px", textAlign: "end" }}>
            <i
              className="bi bi-car-front-fill"
              style={{ fontSize: "20px", color: "rgb(242, 93, 33)" }}
              onClick={vehicleModalHandler}
            ></i>
          </div>
        </span>
        {vehicleData.map((vehicle) => (
          <div
            className="vehicle-card"
            key={`card-${vehicle.id}`}
            role="listitem"
          >
            <div className="vehicle-card__heading">
              <div></div>
            </div>

            <div className="vehicle-card__row">
              <div className="vehicle-card__label">Brand</div>
              <div className="vehicle-card__value">{vehicle.brand_name}</div>
            </div>

            <div className="vehicle-card__row">
              <div className="vehicle-card__label">Model</div>
              <div className="vehicle-card__value">{vehicle.model_name}</div>
            </div>

            <div className="vehicle-card__row">
              <div className="vehicle-card__label">Year</div>
              <div className="vehicle-card__value">{vehicle.make_year}</div>
            </div>

            <div className="vehicle-card__row">
              <div className="vehicle-card__label">Fuel</div>
              <div className="vehicle-card__value">{vehicle.fuel_type}</div>
            </div>

            <div className="vehicle-card__row">
              <div className="vehicle-card__label">Registration</div>
              <div className="vehicle-card__value">
                {vehicle.vehicle_number}
              </div>
            </div>

            <div className="vehicle-card__row">
              <div className="vehicle-card__label">Status</div>
              <div
                className={classNames(
                  "vehicle-card__value",
                  vehicle.status &&
                    vehicle.status.toLowerCase() === "cancelled" &&
                    "status--cancelled"
                )}
              >
                {vehicle.status ?? "—"}
              </div>
            </div>

            <div className="vehicle-card__footer">
              <button
                style={{ borderRadius: "20px" }}
                className={classNames(
                  "customer-vehicle-info__btn",
                  "customer-vehicle-info__btn--edit"
                )}
                onClick={() => handleEdit(vehicle)}
              >
                Edit
              </button>
              <button
                style={{ borderRadius: "20px" }}
                className={classNames(
                  "customer-vehicle-info__btn",
                  "customer-vehicle-info__btn--delete"
                )}
                onClick={() => handleDelete(vehicle)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && editingVehicle && (
        <VehicleEdit
          onClose={handleClose}
          vehicle={editingVehicle}
          fetchVehicle={fetchVehicle}
        />
      )}

      {/*  */}

      {showDeleteModal && (
      <DeleteModal
  message={
    <>
    <span style={{color: "#3071E4", fontFamily: "Karbon-medium", fontSize: "18px"}}>
      Are you sure? 
      </span>
      <br />

      <br />
      <span style={{ fontFamily: "Karbon-medium", fontSize: "16px"}}>
      Do you really want to delete these records? This process cannot be undone.
      </span>
    </>
  }  onConfirm={confirmDelete}
  onCancel={cancelDelete}
  handleCloseModal={handleClose}
/>

      )}
    </div>
  );
};

export default VehicleTable;
